<script>
	import { page } from '$app/stores';
	import { PWA } from '$lib/pwa.js';
	import { auth } from '$lib/auth.svelte.js';

	let drawerOpen = $state(false);

	const navItems = [
		{ label: 'Home', href: '/', icon: 'home' },
		{ label: 'Events', href: '/events', icon: 'events' },
		{ label: 'Articles', href: '/articles', icon: 'articles' },
		{ label: 'Watch', href: '/watch', icon: 'watch' },
	];

	const drawerSections = $derived([
		{
			heading: 'Explore',
			links: [
				{ label: 'Photo Gallery', href: '/gallery' },
				// Only advertise the install page once we publicly invite installs.
				...(PWA.inviteInstall ? [{ label: 'Add App to Home Screen', href: '/install' }] : []),
			],
		},
		{
			heading: 'About',
			links: [
				{ label: 'About Our Church', href: '/about' },
				{ label: 'Patron Saint of Our Church', href: '/patron-saint' },
				{ label: 'Our Pastor', href: '/pastor' },
				{ label: 'Armenian Church Origin', href: '/armenian-church-origin' },
				{ label: 'Church Services', href: '/services' },
				{ label: 'Get In Touch', href: '/contact' },
			],
		},
		{
			heading: 'Faith',
			links: [
				{ label: 'What We Believe', href: '/faith' },
				{ label: 'Saints & Feast Days', href: '/faith/feast-days' },
				{ label: 'Saints of the Armenian Church', href: '/faith/saints' },
			],
		},
		{
			heading: 'Community',
			links: [
				{ label: 'ACYOA Juniors', href: '/community/acyoa-juniors' },
				// { label: 'Adult Choir', href: '/community/adult-choir' },
				// { label: 'Altar Servers', href: '/community/altar-servers' },
				// { label: 'Chess Club', href: '/community/chess-club' },
				// { label: 'Cultural Committee', href: '/community/cultural-committee' },
				// { label: 'Kirikian Armenian School', href: '/community/kirikian-armenian-school' },
				// { label: 'Parish Council', href: '/community/parish-council' },
				// { label: 'Sunday School', href: '/community/sunday-school' },
				// { label: 'Youth Orchestra', href: '/community/youth-orchestra' },
			],
		},
		{
			// Member portal. Invite-only, Parish Council first; relabel the signed-out label to
			// "Member Login" when sign-in opens to the general membership.
			heading: 'Members',
			links: [
				auth.member || auth.hint
					? { label: 'Member Area', href: '/members' }
					: { label: 'Parish Council Login', href: '/login' },
			],
		},
	]);

	function closeDrawer() {
		drawerOpen = false;
	}
</script>

<!-- Bottom nav bar -->
<nav class="fixed bottom-0 left-0 right-0 z-40 border-t border-stone-dark bg-altar-white pb-[env(safe-area-inset-bottom)] md:hidden">
	<div class="flex items-stretch justify-around">
		{#each navItems as item}
			<a
				href={item.href}
				class="flex min-h-[60px] flex-1 flex-col items-center justify-center gap-0.5 py-2 text-xs transition-colors {$page.url.pathname === item.href ? 'text-wine font-medium' : 'text-navy-muted'}"
			>
				{#if item.icon === 'home'}
					<svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width={$page.url.pathname === item.href ? 2.5 : 1.5}>
						<path stroke-linecap="round" stroke-linejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-4 0a1 1 0 01-1-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 01-1 1h-2z" />
					</svg>
				{:else if item.icon === 'events'}
					<svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width={$page.url.pathname === item.href ? 2.5 : 1.5}>
						<path stroke-linecap="round" stroke-linejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
					</svg>
				{:else if item.icon === 'articles'}
					<svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width={$page.url.pathname === item.href ? 2.5 : 1.5}>
						<path stroke-linecap="round" stroke-linejoin="round" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 12h10" />
					</svg>
				{:else if item.icon === 'watch'}
					<svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width={$page.url.pathname === item.href ? 2.5 : 1.5}>
						<path stroke-linecap="round" stroke-linejoin="round" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
						<path stroke-linecap="round" stroke-linejoin="round" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
					</svg>
				{/if}
				<span>{item.label}</span>
			</a>
		{/each}

		<!-- More button -->
		<button
			class="flex min-h-[60px] flex-1 flex-col items-center justify-center gap-0.5 py-2 text-xs transition-colors {drawerOpen ? 'text-wine font-medium' : 'text-navy-muted'}"
			onclick={() => drawerOpen = !drawerOpen}
			aria-label="More navigation"
		>
			<svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width={drawerOpen ? 2.5 : 1.5}>
				<path stroke-linecap="round" stroke-linejoin="round" d="M5 12h.01M12 12h.01M19 12h.01M6 12a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0z" />
			</svg>
			<span>More</span>
		</button>
	</div>
</nav>

<!-- More drawer overlay -->
{#if drawerOpen}
	<!-- Backdrop -->
	<!-- svelte-ignore a11y_no_static_element_interactions -->
	<div
		class="fixed inset-0 z-50 bg-navy/50 md:hidden"
		onclick={closeDrawer}
		onkeydown={(e) => e.key === 'Escape' && closeDrawer()}
		role="presentation"
	></div>

	<!-- Drawer panel -->
	<div class="fixed inset-x-0 bottom-0 z-50 max-h-[70vh] overflow-y-auto rounded-t-2xl bg-altar-white pb-[calc(env(safe-area-inset-bottom)+80px)] md:hidden" style="animation: slideUp 200ms ease-out">
		<!-- Handle -->
		<div class="flex justify-center py-3">
			<div class="h-1 w-10 rounded-full bg-stone-dark"></div>
		</div>

		<!-- Navigation sections -->
		<div class="px-6 pb-6">
			{#each drawerSections as section}
				<div class="mb-4">
					<h3 class="mb-1 text-xs font-semibold tracking-wider text-navy-muted uppercase">{section.heading}</h3>
					{#each section.links as link}
						<a
							href={link.href}
							class="block py-3 text-base transition-colors {$page.url.pathname === link.href ? 'text-wine font-medium' : 'text-navy hover:text-wine'}"
							onclick={closeDrawer}
						>
							{link.label}
						</a>
					{/each}
				</div>
			{/each}
		</div>
	</div>
{/if}

<style>
	@keyframes slideUp {
		from { transform: translateY(100%); }
		to { transform: translateY(0); }
	}
</style>
